import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  Percent,
  Trophy,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { reportCardsApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'

const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(date)
}

const sanitizeFileName = (value) => (
  String(value || 'document')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
)

function MetricCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  )
}

export default function MarksheetPage() {
  const { studentId, examId } = useParams()
  const [marksheet, setMarksheet] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloadLoading, setDownloadLoading] = useState(false)

  useEffect(() => {
    const fetchMarksheet = async () => {
      if (!studentId || !examId) {
        setMarksheet(null)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const data = await reportCardsApi.getMarksheet(studentId, examId)
        setMarksheet(data)
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load marksheet')
        setMarksheet(null)
      } finally {
        setLoading(false)
      }
    }

    fetchMarksheet()
  }, [examId, studentId])

  const student = marksheet?.student || {}
  const exam = marksheet?.exam || {}
  const result = marksheet?.result || {}

  const studentName = useMemo(() => (
    `${student?.firstName || ''} ${student?.lastName || ''}`.trim() || student?.name || 'Student'
  ), [student?.firstName, student?.lastName, student?.name])

  const backLink = studentId && marksheet?.exam?.sessionId
    ? `/report-cards/${encodeURIComponent(studentId)}/${encodeURIComponent(marksheet.exam.sessionId)}`
    : studentId
      ? `/report-cards?studentId=${encodeURIComponent(studentId)}`
      : '/report-cards'

  const handleDownload = async () => {
    if (!studentId || !examId) return

    try {
      setDownloadLoading(true)
      const response = await reportCardsApi.downloadMarksheet(studentId, examId)
      const objectUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `${sanitizeFileName(`marksheet-${student?.admissionNo || studentId}-${exam?.title || examId}`)}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
      toast.success('Marksheet downloaded')
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download marksheet')
    } finally {
      setDownloadLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
      </div>
    )
  }

  if (!marksheet) {
    return (
      <div className="card p-8 text-center">
        <FileText className="mx-auto h-10 w-10 text-gray-200" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Marksheet not available</h2>
        <p className="mt-2 text-sm text-gray-500">The exam marksheet could not be loaded.</p>
        <Link to={backLink} className="btn-secondary mt-5 inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Marksheet"
        subtitle={`Detailed exam result for ${studentName}.`}
        chips={[
          exam?.subject || 'Subject not set',
          exam?.formattedDate || formatDate(exam?.date),
          student?.className || student?.class || 'Class not assigned',
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={handleDownload} className="btn-primary flex items-center gap-2" disabled={downloadLoading}>
              {downloadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download PDF
            </button>
            <Link to={backLink} className="btn-secondary inline-flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
          </div>
        }
      />

      <div className="overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-900 to-primary-900 p-6 text-white shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">Exam snapshot</p>
        <h3 className="mt-3 text-3xl font-bold tracking-tight">{exam?.title || 'Exam Result'}</h3>
        <p className="mt-2 text-sm text-white/80">
          {exam?.subject || 'Subject not set'} · {exam?.formattedDate || formatDate(exam?.date)}
        </p>
        <p className="mt-5 text-sm text-white/70">
          {studentName} · {student?.className || student?.class || 'Class not assigned'}
          {student?.admissionNo ? ` · Adm. ${student.admissionNo}` : ''}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={CheckCircle2}
          label="Score"
          value={`${result?.marksObtained ?? '--'} / ${exam?.totalMarks ?? '--'}`}
          tone="bg-blue-50 text-blue-700"
        />
        <MetricCard
          icon={Percent}
          label="Percentage"
          value={`${Math.round(result?.percentage || 0)}%`}
          tone="bg-emerald-50 text-emerald-700"
        />
        <MetricCard
          icon={Trophy}
          label="Grade"
          value={result?.grade || '--'}
          tone="bg-amber-50 text-amber-700"
        />
        <MetricCard
          icon={CheckCircle2}
          label="Result"
          value={result?.isPassed ? 'Pass' : 'Fail'}
          tone={result?.isPassed ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="card p-5">
          <h3 className="text-lg font-bold text-gray-900">Result Details</h3>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">
              Status {result?.status || 'draft'}
            </span>
            <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700">
              Attendance {result?.attendanceStatus || 'present'}
            </span>
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">
              Pass mark {exam?.passMarks ?? '--'}
            </span>
          </div>

          {result?.feedback && (
            <div className="mt-5 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Feedback</p>
              <p className="mt-2 text-sm text-gray-700">{result.feedback}</p>
            </div>
          )}

          {result?.remarks && (
            <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Remarks</p>
              <p className="mt-2 text-sm text-gray-700">{result.remarks}</p>
            </div>
          )}
        </div>

        <div className="card p-5">
          <h3 className="text-lg font-bold text-gray-900">Exam Meta</h3>
          <div className="mt-5 space-y-4">
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Student</p>
              <p className="mt-2 text-sm font-medium text-gray-900">{studentName}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Exam Date</p>
              <p className="mt-2 text-sm font-medium text-gray-900">{exam?.formattedDate || formatDate(exam?.date)}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Total Marks</p>
              <p className="mt-2 text-sm font-medium text-gray-900">{exam?.totalMarks ?? '--'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
